import React, { useState, useEffect } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Search, Wrench } from 'lucide-react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

// Import all settings components
import ProfileSettings from './ProfileSettings';
import PreferencesSettings from './PreferencesSettings';
import NotificationsSettings from './NotificationsSettings';
import GeneralSettings from './GeneralSettings';
import UsersSettings from './UsersSettings';
import SeatsSettings from './SeatsSettings';
import TeamsSettings from './TeamsSettings';
import BillingSettings from './BillingSettings';
import ConnectionsSettings from './ConnectionsSettings';
import WorkflowsSettings from './WorkflowsSettings';
import RolesPermissionsSettings from './RolesPermissionsSettings';
import DataTransfersSettings from './DataTransfersSettings';
import SystemSettings from './SystemSettings';
import IntegrationsSettings from './IntegrationsSettings';
const AutomationBuilder = React.lazy(() => import('../../../QMS/pages/AutomationBuilder'));
import UsersPermissions from './UsersPermissions';
import RubricBuilder from './RubricBuilder';
import CompanySettings from './CompanySettings';

// ─── Sidebar Structure ─────────────────────────────────────────────
const SIDEBAR_SECTIONS = [
    {
        title: null,
        items: [
            { id: 'general', label: 'General', path: '/settings/general', icon: "settings" },
            { id: 'notifications', label: 'Notifications', path: '/settings/notifications', icon: "notifications" },
            { id: 'user-preferences', label: 'User Preferences', path: '/settings/user-preferences', icon: "person" },
            { id: 'data-transfers', label: 'Data Transfers', path: '/settings/data-transfers', icon: "assignment_turned_in" },
        ]
    },
    {
        title: 'Workspace Administration',
        items: [
            { id: 'admin-company', label: 'Workspace & Branding', path: '/settings/admin/company', icon: "domain" },
            { id: 'admin-users', label: 'Users & Permissions', path: '/settings/admin/users', icon: "group" },
            { id: 'admin-roles', label: 'Roles & Access', path: '/settings/admin/roles', icon: "admin_panel_settings" },
        ]
    },

    {
        title: 'Quality Management',
        items: [
            { id: 'qms-rubrics', label: 'Rubrics & Scorecards', path: '/settings/qms/rubrics', icon: "list_alt" },
            { id: 'qms-audit', label: 'Audit Settings', path: '/settings/qms/audit', icon: "assignment_turned_in" },
            { id: 'qms-calibration', label: 'Calibration Rules', path: '/settings/qms/calibration', icon: "balance" },
            { id: 'qms-compliance', label: 'Compliance Rules', path: '/settings/qms/compliance', icon: "gavel" },
            { id: 'qms-rca', label: 'RCA Templates', path: '/settings/qms/rca-templates', icon: "hub" },
            { id: 'qms-coaching', label: 'Coaching Templates', path: '/settings/qms/coaching-templates', icon: "psychology" },
            { id: 'qms-iso', label: 'ISO Mapping', path: '/settings/qms/iso-mapping', icon: "verified" },
        ]
    },
    {
        title: 'Automation',
        items: [
            { id: 'auto-workflows', label: 'Workflow Builder', path: '/settings/automation/workflows', icon: "schema" },
            { id: 'auto-triggers', label: 'Triggers', path: '/settings/automation/triggers', icon: "bolt" },
            { id: 'auto-notifications', label: 'Notifications', path: '/settings/automation/notifications', icon: "campaign" },
        ]
    }
];

// ─── Horizontal Tab Mapping (only for pages that genuinely need sub-tabs) ───
const TABS_MAPPING = {
};

// ─── Pages that need full-width content (no maxWidth cap) ───
const FULL_WIDTH_CATEGORIES = [];

// ─── Placeholder for pages not yet built ───
const PlaceholderSettings = ({ title, description }) => (
    <div className="animate-fade-in" style={{ padding: '48px 0', textAlign: 'center' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Wrench size={32} className="text-muted-foreground" />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>{title}</h2>
        <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', maxWidth: '400px', margin: '0 auto', lineHeight: 1.5 }}>
            {description || 'This configuration section is currently under development.'}
        </p>
        <div style={{ marginTop: '24px', padding: '8px 20px', background: 'var(--bg-muted)', borderRadius: '100px', fontSize: '12px', fontWeight: '600', color: 'var(--text-tertiary)', display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Coming Soon
        </div>
    </div>
);

export default function SettingsLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');

    // Flatten all sidebar items for lookup
    const allItems = SIDEBAR_SECTIONS.flatMap(s => s.items);

    // Determine the active sidebar item based on URL
    const getActiveId = () => {
        const path = location.pathname;
        // Find the longest matching path (most specific match first)
        let bestMatch = null;
        let bestLength = 0;
        for (const item of allItems) {
            if (path.startsWith(item.path) && item.path.length > bestLength) {
                bestMatch = item.id;
                bestLength = item.path.length;
            }
        }
        return bestMatch || 'general';
    };

    const getTabCategory = () => {
        const path = location.pathname;
        if (path.startsWith('/settings/account-defaults')) return 'account-defaults';
        return null;
    };

    const activeId = getActiveId();
    const tabCategory = getTabCategory();
    const activeTabs = tabCategory ? (TABS_MAPPING[tabCategory] || []) : [];
    const isFullWidth = FULL_WIDTH_CATEGORIES.includes(tabCategory || activeId);

    // Redirect to the first sub-path when a base route is hit
    useEffect(() => {
        const path = location.pathname;
        if (path === '/settings' || path === '/settings/') {
            navigate('/settings/general', { replace: true });
        } else if (path === '/settings/account-defaults' || path === '/settings/account-defaults/') {
            navigate('/settings/account-defaults/general', { replace: true });
        } else if (path.startsWith('/settings/users')) {
            navigate('/admin/users', { replace: true });
        } else if (path.startsWith('/settings/billing')) {
            navigate('/admin/billing', { replace: true });
        }
    }, [location.pathname, navigate]);

    const lastPath = localStorage.getItem('oblique_last_path') || '/hub';
    
    // Filter sidebar items by search
    const filteredSections = searchQuery
        ? SIDEBAR_SECTIONS.map(section => ({
            ...section,
            items: section.items.filter(item =>
                item.label.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })).filter(section => section.items.length > 0)
        : SIDEBAR_SECTIONS;

    return (
        <div className="flex-1 flex flex-col min-w-0 bg-surface overflow-hidden">
                {/* Horizontal Tab Bar — only shown when the active page has sub-tabs */}
                {activeTabs.length > 1 && (
                    <div className="border-b px-8 flex items-center gap-6 h-12 shrink-0 overflow-x-auto whitespace-nowrap custom-scrollbar settings-tab-bar">
                        {activeTabs.map(tab => {
                            const isActive = location.pathname === tab.path || location.pathname.startsWith(tab.path + '/');
                            return (
                                <NavLink
                                    key={tab.id}
                                    to={tab.path}
                                    className={`relative flex items-center h-full text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                                        isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {tab.label}
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTabSettings"
                                            className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-t-sm"
                                        />
                                    )}
                                </NavLink>
                            );
                        })}
                    </div>
                )}

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar settings-content-area">
                    <div className={`py-10 w-full ${isFullWidth ? "px-8" : "max-w-[1200px] mx-auto px-8 lg:px-12"}`}>
                        <React.Suspense fallback={
                            <div className="p-12 text-center text-sm text-[var(--text-tertiary)] flex flex-col items-center justify-center">
                                <div className="spinner-sm mb-2 [border-top-color:var(--primary)]" />
                                Loading settings...
                            </div>
                        }>
                        <Routes>
                            {/* ── Top-level pages ── */}
                            <Route path="general" element={<PreferencesSettings />} />
                            <Route path="notifications" element={<NotificationsSettings />} />
                            <Route path="user-preferences" element={<ProfileSettings />} />
                            <Route path="data-transfers" element={<DataTransfersSettings currentCompany={{ id: 'DEFAULT', name: 'Oblique Workspace' }} />} />



                            {/* ── Workspace Administration ── */}
                            <Route path="admin/company" element={<CompanySettings userRole="admin" />} />
                            <Route path="admin/users" element={<UsersPermissions />} />
                            <Route path="admin/roles" element={<PlaceholderSettings title="Roles & Access" description="Define custom RBAC profiles for different modules." />} />
                            <Route path="workspace" element={<Navigate to="/settings/admin/company" replace />} />
                            <Route path="company" element={<Navigate to="/settings/admin/company" replace />} />

                            {/* ── Quality Management ── */}
                            <Route path="qms/rubrics" element={<RubricBuilder />} />
                            <Route path="qms/audit" element={<PlaceholderSettings title="Audit Settings" description="Configure default rubric templates, scoring scales, auto-fail thresholds, and audit types." />} />
                            <Route path="qms/calibration" element={<PlaceholderSettings title="Calibration Rules" description="Set maximum variance thresholds, minimum calibrations per month, and evaluator rotation rules." />} />
                            <Route path="qms/compliance" element={<PlaceholderSettings title="Compliance Rules" description="Configure compliance checkboxes, mandatory fields, and auto-escalation triggers." />} />
                            <Route path="qms/rca-templates" element={<PlaceholderSettings title="RCA Templates" description="Manage 5-Why, Fishbone, and custom root cause analysis templates." />} />
                            <Route path="qms/coaching-templates" element={<PlaceholderSettings title="Coaching Templates" description="Configure session templates, follow-up cadence, and improvement goal tracking." />} />
                            <Route path="qms/iso-mapping" element={<PlaceholderSettings title="ISO Mapping" description="Map audit rubric items to ISO 9001 clauses and generate compliance gap reports." />} />

                            {/* ── Automation ── */}
                            <Route path="automation" element={<AutomationBuilder />} />
                            <Route path="automation/ai" element={<PlaceholderSettings title="AI Audit Assistant" description="Configure AI model selection, prompt customization, auto-audit rules, and credit usage." />} />
                            <Route path="automation/workflows" element={<WorkflowsSettings />} />
                            <Route path="automation/triggers" element={<PlaceholderSettings title="Triggers" description="Set up event-based triggers for automated actions across the platform." />} />
                            <Route path="automation/notifications" element={<PlaceholderSettings title="Automation Notifications" description="Configure workflow notification rules, escalation chains, and channel routing." />} />

                            {/* ── Integrations ── */}
                            <Route path="integrations/hubspot" element={<ConnectionsSettings />} />
                            <Route path="integrations/salesforce" element={<PlaceholderSettings title="Salesforce" description="Connect your Salesforce org for contact sync, deal tracking, and field mapping." />} />
                            <Route path="integrations/google-meet" element={<PlaceholderSettings title="Google Meet" description="Configure calendar sync, auto-recording, and transcript extraction for Google Meet." />} />
                            <Route path="integrations/zoom" element={<PlaceholderSettings title="Zoom" description="Connect your Zoom account for recording auto-pull and transcript integration." />} />

                            {/* ── Legacy redirects ── */}
                            <Route path="preferences/*" element={<Navigate to="/settings/general" replace />} />
                            <Route path="account/*" element={<Navigate to="/settings/account-defaults/general" replace />} />
                            <Route path="apps/*" element={<Navigate to="/settings/integrations/hubspot" replace />} />
                            <Route path="workflows/*" element={<Navigate to="/settings/automation/workflows" replace />} />
                        </Routes>
                        </React.Suspense>
                    </div>
                </div>
            </div>
    );
}
